import React, { ReactElement } from 'react';
import {
  GridCellProps,
  GridColumn,
  GridColumnReorderEvent,
  GridColumnResizeEvent,
  GridHeaderCellProps,
  GridRowProps,
  GridSortChangeEvent
} from "@progress/kendo-react-grid";
import {
  Component,
  Container,
  HBox,
  NavigationGrid,
  Pinger,
  Position,
  PuzzleButton, PuzzleCheckbox,
  PuzzleFakeCheckbox,
  PuzzleTooltip as TpTooltip
} from "puzzle";
import {
  checkVerticalScroll, commonLocalizationGetter,
  defaultCellRender,
  getMappedSubmissionStatuses, getTableTdAccessibilityProps,
  setArrowNavigation, translateCustomAttributes,
} from "../../util/Util";
import { CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import Drawer from "../../Drawer/Drawer";
import ContextMenu from "./ContextMenu/ContextMenu";
import classNames from 'classnames';
import * as _ from 'lodash';
import {
  COLUMN,
  COLUMN_MENU_PROPS,
  MAIN_GRID_PROPS,
  MAIN_GRID_STATE,
  OWNER,
  READER_COLUMN,
  SUBMISSION
} from "./Types";
import { SortDescriptor } from "@progress/kendo-data-query";
import { Popup } from "@progress/kendo-react-popup";
import { PREFERENCES_DATA } from "../../Preferences/Types";
import ColumnMenuWrapper from "puzzle/components/grid/columnmenu/ColumnMenuWrapper";
import DragAndDropWrapper from "../../UI/DragAndDrop/DragAndDropWrapper";
import { ViewContext } from "../../Header/ViewOption/Context/ViewProvider";
import SearchBar from "../../SubmissionsList/SearchBar/SearchBar";
import ActionToolbar from "../../SubmissionsList/ActionToolbar/ActionToolbar";
import Filters from "../../Dashboard/Filters/Filters";
import { isUndefined } from "lodash";
import OpenMailLink from "../../util/OpenMailLink/OpenMailLink";
import { EMAIL_CLIENT_PARAMS } from "../../util/OpenMailLink/Types";
import { PHASE_OWNER } from "../../Drawer/Tabs/Types";
import PoliciesManager from "../../util/Policies";
import { NAVIGATION_GRID_PROPS } from "puzzle/components/grid/Types";

//All fields that are date related should be added here so grid can be filtered by them
export const DateFields = {
  dueDate: "dueDate",
  dateCreated: "dateCreated",
};

const budgetStatusesOrder: string[] = ['REQUESTED', 'COUNTERED', 'ACCEPTED', 'UPDATED', 'APPROVED', 'PO_SENT', 'INVOICING_AVAILABLE', 'FINALIZED'];

class Grid<GENERIC_PROPS, GENERIC_STATE> extends Component<GENERIC_PROPS & MAIN_GRID_PROPS, GENERIC_STATE & MAIN_GRID_STATE> {
  columnPopupArrowIndex = 0;
  tooltip: any;
  mainGridColumnSettings = React.createRef<PuzzleButton>();
  settingsButtonRef: any;
  gridSettingsDropdownRef: any;
  gridRef: any;
  columnResizeTimer: any;
  // Flag that describes if an arrow key is pressed and held
  holdingArrowKeys = false;
  sortableColumns: string[] = [];
  // For now, only used for setting sort options to cookie
  userName: string = '';
  refreshPinger: any;
  gridConfigTemp: any = {};
  chartFiltersConfiguration: COLUMN[] = []; // used for saving chart filter configuration, need to added on top of columns when calling /folderViewConfiguration

  gridDomElement: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  /**
   * Show/hide checkbox selection column
   */
  checkboxSelectionEnabled: boolean = false;
  /**
   * Used with dueSoonOverDue component to force different sort to folderViewConfig
   */
  overrideSort: null | { dir: string, field: string } = null;

  static defaultProps: MAIN_GRID_PROPS = {
    additionalClass: '',
    compactRowHeight: 32,
    favoritesOnly: false,
    nonReordeableColumns: [], //define in child component
    nonResizableColumns: [], //define in child component
    pageSize: 50,
    quickFiltersEnabled: false,
    rowHeight: 48,
    selectedFieldName: 'selected',
    searchBarConfig: {
      pageTitle: 'NonLocalized Page Title',
      pageTitleCount: () => 'NonLocalized Page Title Count',
      isClientView: false,
      initializeUserData: {},
      isDrawerOpen: false,
      filtersAsSearchBar: false,
      renderSearchBar: undefined
    },
    setDynamicSize: () => {
    },
    sortable: true,
    taskList: '',
    templatesOnly: false,
    detached: false,
    emptyGridConfig: {},
    defaultSort: [{
      field: '',
      dir: ''
    }],
    actionsNames: { //define in child component
      filter: '',
      refreshGrid: '',
      refreshGridOnCurrentPosition: ''
    },
    getDataUrl: '',
    uniqueField: '',
    excludeFirstRequest: true,
    checkboxSelection: false
  };
  public state: MAIN_GRID_STATE = {
    activeSearchbar: false,
    checkboxSelectionData: [],
    data: [],
    dueSoon: 24,
    dynamicColumnWidth: 0,
    columns: [],
    filterConfigData: {},
    offset: {
      left: 0,
      top: 0
    },
    openFilter: '',
    orders: 0,
    quickFiltersData: [],
    quickFiltersVisible: false,
    selectedData: [],
    selectedSubmission: undefined,
    sort: this.props.defaultSort,
    tasks: {},
    dragging: false,
    showGridColumnSettings: false,
    customFieldsMappers: [],
    keyboardShortcuts: true,
    drawerOpen: false,
    warningBannerMessage: '',
    lastSelectedCheckboxIndex: null,
    selectionLimit: 0
  };
  menuActionWidth = '100px';

  constructor(props: any) {
    super(props);
    this.checkboxSelectionEnabled = props.checkboxSelection && PoliciesManager.hasMassActions;
  }

  /**
   * Additional grid title text. Override in child component to show text
   */
  additionalGridTitle = () => {
    return (
      <>
        {this.props.gridFilter.renderFilterHeader()}
      </>
    )
  }

  /**
   * Calculates width of columns that needs to fill remaining space on grid
   * Width is set to columns that initially have value equal to 0
   */
  calculateDynamicColumnWidth = (columns: any[]) => {
    let numberOfColumnsToSpread = 0;
    let totalWidth = 0;
    columns.forEach(({ hidden, width }: any) => {
      if (!hidden) {
        if (width === 0) numberOfColumnsToSpread++;
        else totalWidth += width;
      }
    });
    const gridRef = this.gridDomElement.current;
    const width = numberOfColumnsToSpread > 0 && gridRef ? Math.max(Math.floor((gridRef.offsetWidth - totalWidth) / numberOfColumnsToSpread), 100)
      : 100;
    return width;
  };

  /**
   * Checkbox selection change handler
   */
  checkboxSelectionChange = (dataItem: any, field: string, event: CheckboxChangeEvent) => {
    let { uniqueField } = this.props;
    let { checkboxSelectionData = [], lastSelectedCheckboxIndex, selectionLimit } = this.state;
    let { value } = event;

    let shiftKey = event.nativeEvent.shiftKey;
    if (shiftKey && _.isNumber(lastSelectedCheckboxIndex)) {
      let newSelectedIndex = dataItem.Index;
      let i = newSelectedIndex;
      let loopEnd = lastSelectedCheckboxIndex;
      let data = this.gridRef.gridRef.state.data;
      if (newSelectedIndex > lastSelectedCheckboxIndex) {
        i = lastSelectedCheckboxIndex;
        loopEnd = newSelectedIndex;
      }
      while (i <= loopEnd) {
        let current = data[i];
        if (value) {
          checkboxSelectionData.push(current);
        } else {
          _.remove(checkboxSelectionData, { [uniqueField]: current[uniqueField] });
        }
        current.checkboxSelection = value;
        i++;
      }
    } else {
      if (value) {
        checkboxSelectionData?.push(dataItem);
      } else {
        _.remove(checkboxSelectionData, { [uniqueField]: dataItem[uniqueField] });
      }
      dataItem.checkboxSelection = value;
    }

    checkboxSelectionData = _.uniqBy(checkboxSelectionData, uniqueField);
    let warningBannerMessage = checkboxSelectionData.length > selectionLimit ? this.translate('selectionExceeded', { selectionLimit }) : '';

    // @ts-ignore
    this.setState({ checkboxSelectionData, warningBannerMessage, lastSelectedCheckboxIndex: dataItem.Index });
    this.dispatch('setCheckboxSelected', '', {
      selectedItems: checkboxSelectionData,
      forceDrawerClose: checkboxSelectionData.length > 1
    });
  }

  /**
   * Used to change chart filter configuration
   * @param dataIndex
   * @param hidden
   */
  changeChartFolderViewConfiguration = (dataIndex: string, hidden: boolean) => {
    const index = _.findIndex(this.chartFiltersConfiguration, { dataIndex });
    if (index > -1) {
      this.chartFiltersConfiguration[index].hidden = hidden;
    }
  };

  /**
   * Clear all checkbox selected items
   */
  checkboxSelectionClear = () => {
    if (this.checkboxSelectionEnabled) {
      let { checkboxSelectionData } = this.state;
      if (checkboxSelectionData?.length) {
        let { data } = this.gridRef.gridRef.state;
        let newData = data.map((item: any) => {
          item.checkboxSelection = false;
          return item;
        })
        this.gridRef.gridRef.setState({
          data: newData
        });

        // @ts-ignore
        this.setState({
          checkboxSelectionData: [],
          warningBannerMessage: ''
        });


        this.dispatch('setCheckboxSelected', Position.end, {
          selectedItems: [],
          forceDrawerClose: false
        });
      }
    }
  }

  /**
   * CheckboxSelection cell renderer
   * @param {GridCellProps} cellProps
   */
  checkboxSelectionCellRenderer = (cellProps: GridCellProps) => {
    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let { dataItem, field } = cellProps;

    let checked = dataItem[field || ''];
    return (
      loadingCell ? loadingCell : (
        <td
          className={'k-text-center tp-pointable pd-checkbox-selection-cell'}
          {...tdProps}
        >
          <PuzzleCheckbox
            checked={!!checked}
            onChange={this.checkboxSelectionChange.bind(this, dataItem, field)}
          />
        </td>
      )
    );
  }

  /**
   * Clears filter and refreshes data
   * @param withRefresh
   */
  clearFilter = (withRefresh: boolean = true) => {
    this.setState({ quickFiltersData: [] });
    if (withRefresh) {
      this.dispatch(this.props.actionsNames.filter, '', { mainGridFilter: {} });
    }
  };

  /**
   * Close settings dropdown
   */
  closeGridSettingsDropdown = (event: any) => {
    if (!this.gridSettingsDropdownRef?.contains(event.target) && !this.settingsButtonRef?.contains(event.target)) {
      // @ts-ignore
      this.setState({ showGridColumnSettings: false });
    }
  };
  /**
   * Sets id of row as ariaControls parameter to the element that is controlled by row.
   * @param row
   */
  private handleRowControls: Function = (row: ReactElement<HTMLTableRowElement>) => {
    let drawer = document && document.getElementById(Drawer.drawerId);
    if (drawer) {
      drawer.setAttribute('aria-labelledby', _.get(row, 'id'));
    }
  };

  /**
   * Sets keydown listener and focuses first element
   *
   * @param el
   */
  columnsPopupOnOpen = (el: any) => {
    let columnPopupRef = el.target?._popup;
    if (columnPopupRef) {
      // @ts-ignore
      const checkboxList = columnPopupRef.querySelectorAll('.k-menu-item');
      columnPopupRef.addEventListener('keydown', (e: any) => setArrowNavigation(this, checkboxList, e));
      checkboxList[this.columnPopupArrowIndex].focus();
    }
  };

  /**
   * Component will unmount
   * - remove window resize listener
   * - remove persistent action data
   */
  componentWillUnmount(): void {
    super.componentWillUnmount();
    window.removeEventListener('resize', this.windowOnResize);
    window.removeEventListener('click', this.closeGridSettingsDropdown);
    this.updateFolderViewConfiguration();
    this.refreshPinger?.stopPinging();
  }

  /**
   * Returns column configuration depending on dataIndex and filter configuration for given filed
   */
  createGridColumn(dataIndex: string, header: string, cellRender: Function, headerCell: any, width: number | undefined, sortable: boolean = false, orderIndex: number) {
    const {
      gridFilter,
      nonReordeableColumns,
      nonResizableColumns,
      favoritesOnly,
      quickFiltersEnabled = false
    } = this.props;
    const {
      filterConfigData = [],
      quickFiltersData
    } = this.state;
    let dataIndexSplit = dataIndex.split('.')[0];

    let columnProps: any = {
      field: dataIndex,
      key: dataIndex,
      minResizableWidth: 100,
      orderIndex,
      title: header,
      headerCell,
      cell: cellRender,
      filterable: false,
      width: width || this.state.dynamicColumnWidth,
      reorderable: !_.includes(nonReordeableColumns, dataIndex),
      resizable: !_.includes(nonResizableColumns, dataIndex),
      columnMenu: (quickFiltersEnabled && filterConfigData.find((item: any) => dataIndexSplit === item.filterName)) || sortable ? gridFilter.renderColumnMenu : null
    };

    if (['usageCount', 'projectId'].includes(dataIndex)) {
      Object.assign(columnProps, {
        className: 'k-text-center',
        headerClassName: columnProps.headerClassName + ' k-text-center'
      });
    }

    if (['instructions', 'notes'].includes(dataIndex)) {
      Object.assign(columnProps, {
        className: 'k-text-center',
        headerClassName: columnProps.headerClassName + ' k-text-center',
        minResizableWidth: 58,
        width: columnProps.width < 58 ? 58 : columnProps.width,
      });
    }

    // this needs to stay because these columns are both sortable and filterable, the text ellipsis won't be visible
    // if we do not increase their minimum resizable width
    if (['fileCount', 'wordCount'].includes(dataIndex)) {
      Object.assign(columnProps, {
        minResizableWidth: 100,
        width: columnProps.width < 100 ? 100 : columnProps.width,
      });
    }

    if (dataIndex === 'isFavorite') {
      Object.assign(columnProps, {
        className: 'k-text-center',
        width: '40px',
        headerClassName: (columnProps.headerClassName || '') + ' k-text-center tp-favorite-submission-cell',
      });
    }

    if (dataIndex === 'menuAction') {
      Object.assign(columnProps, {
        locked: true,
        className: 'k-text-right',
        headerClassName: 'k-text-right',
        title: this.translate('columnHeaders.actionCell'),
        width: this.menuActionWidth
      });
    }

    if (dataIndex === 'statusColor') {
      Object.assign(columnProps, {
        headerClassName: 'pd-status-color-column-header-cell',
        width: '16px'
      });
    }

    if (dataIndex === 'checkboxSelection') {
      Object.assign(columnProps, {
        headerClassName: 'pd-checkbox-selection-header-cell',
      });
    }

    const isCustom = _.find(this.state.customFieldsMappers, { 'name': dataIndex });
    if (isCustom) {
      Object.assign(columnProps, {
        title: translateCustomAttributes(header)
      });
    }

    Object.assign(columnProps, {
      headerClassName: classNames(
        `${columnProps.headerClassName || ''}`, {
          'tp-sortable': sortable,
          'tp-filter-applied': !!quickFiltersData.find((item: any) => dataIndexSplit === item.field),
          'tp-filter-opened': dataIndexSplit === this.state.openFilter,
          'tp-grid-header-with-filter': filterConfigData.find((item: any) => dataIndexSplit === item.filterName) && !favoritesOnly
        }
      ),
      className: classNames(columnProps.className, {
        'tp-sortable': sortable
      })
    });

    if (!columnProps.field) {
      Object.assign(columnProps, {
        field: columnProps.dataIndex,
      });
    }

    return (<GridColumn {...columnProps} />)
  }

  /**
   * Create pinger for grid auto refresh if auto refresh option is enabled in preferences
   */
  createPinger = (preferencesData: PREFERENCES_DATA) => {
    const { autoRefresh } = preferencesData;
    if (autoRefresh) {
      const refreshAction = this.dispatch.bind(this, this.props.actionsNames.refreshGrid);
      this.refreshPinger = new Pinger(300000, refreshAction);
    }
  }

  /**
   * Initially fills sortableColumns array with all sortable column's dataIndexes
   */
  createSortableColumnsArray = (columns: any) => {
    const sortableColumns: string[] = [];
    columns && columns.forEach(({ sortable, dataIndex }: any) => {
      if (sortable) sortableColumns.push(dataIndex)
    });
    return sortableColumns;
  };

  /**
   * Custom fields renderer
   * @param cellProps
   */
  customFieldRender = (cellProps: GridCellProps): ReactElement<HTMLTableCellElement> => {
    const { dataItem, field } = cellProps;
    let valueItem;

    if (field && dataItem.customFields) {
      let element = _.find(this.state.customFieldsMappers, { 'name': field });
      const regex = /\d+/;
      if (element) {
        let regexResult = element.mapping.match(regex);
        if (regexResult) {
          const position = parseInt(regexResult[0]);
          valueItem = dataItem?.customFields[position];
        }
      }
    }

    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    return (
      loadingCell ? loadingCell :
        (
          <td {...tdProps} className={cellProps.className}>
            {valueItem}
          </td>
        )
    );
  };

  /**
   * Add tasks and dispatch selectionChangeToolbar action
   * @param selectedData
   */
  dispatchSelectChangeAction = (selectedData: SUBMISSION[]) => {
    let { tasks } = this.state;
    selectedData.forEach((item: any) => item.tasks = tasks[item.pageIndex]);
    this.dispatch('selectionChangeToolbar', '', {
      selectedSubmissions: selectedData
    });
  };

  /**
   * Drag leave event handler
   * @param event
   */
  dragLeaveHandler = (event: React.DragEvent) => {
    if (this.state.dragging) {
      // @ts-ignore
      this.setState({
        dragging: false
      });
    }
  };

  /**
   * Drag leave event handler
   * @param event
   */
  dragOverHandler = (event: React.DragEvent) => {
    if (!this.state.dragging && !this.state.drawerOpen) {
      // @ts-ignore
      this.setState({
        dragging: true
      });
    }
  };

  /**
   * Returns all checked task ids
   */
  getCheckedTaskIds = () => ({ taskIds: this.state.checkboxSelectionData?.map((data: any) => data.taskId) });

  /**
   * Get url for request
   */
  getDataUrl = () => this.props.getDataUrl;
  /**
   * Get folder for request params
   */
  getFolderParamForRequest = () => '';
  /**
   * Generate grid config json data
   */
  getGridConfigJsonData = () => {
    const excludedColumns = ['checkboxSelection', 'menuAction', 'statusColor'];
    let columns = this.state.columns.reduce((acc: any, curr: COLUMN) => {
      if (!excludedColumns.includes(curr.dataIndex)) {
        const updatedColumn: any = {};
        delete Object.assign(updatedColumn, curr, { 'headerKey': curr.header })['header'];
        delete updatedColumn.field;
        acc = [...acc, updatedColumn]
      }
      return acc;
    }, []);
    columns = [...columns, ...this.chartFiltersConfiguration];
    let { field, dir } = this.overrideSort || this.state.sort[0];
    const isCustom = _.find(this.state.customFieldsMappers, { 'name': field });
    if (isCustom) {
      const customFieldSortProp = _.find(this.state.columns, { dataIndex: field });
      field = customFieldSortProp?.sortProperty || field;
    }

    return {
      folder: this.getFolderParamForRequest(),
      metadata: JSON.stringify({
        ascending: dir === 'asc',
        sortProperty: isCustom ? field : field.split('.')[0],
        gridConfig: _.sortBy(columns, ['orderIndex'])
      })
    };
  };

  /**
   * Create due date tooltip title
   */
  generateDueDateTooltipTitle = (isExpired: boolean, isDueSoon: boolean, dueDate: Date): string => {
    let dueDateString = this.translateByNamespace('date.dateWithTime', { date: new Date(dueDate) });
    let title = '';
    if (isExpired) {
      title = this.translate('dueDateOverdue', { dueDateString });
    }
    if (isDueSoon) {
      title = this.translate('dueDateDueSoon', { dueDateString });
    }
    return title;
  };

  /**
   * Goes through all readerColumns and returns only one's whit customFields.
   * @param readerColumns
   */
  getCustomFieldsMappers = (readerColumns: READER_COLUMN[]) => {
    return readerColumns.filter((item: READER_COLUMN) => {
      return item.mapping.includes('customFields');
    });
  };

  /**
   * Returns config object used for preserving grid selection
   */
  getLastSelectedElement = () => {
    const { selectedData } = this.state;
    let lastSelectedItem = undefined;
    if (selectedData.length) {
      lastSelectedItem = _.omit(_.last(selectedData), 'selected');
    }

    return {
      dataItem: lastSelectedItem,
      nativeEvent: {
        shiftKey: false,
        ctrlKey: false,
        focus: false
      }
    };
  };

  /**
   *  Returns array with created sortDescriptor for specific user
   */
  getSortObject = (sortInfo: any): SortDescriptor[] => {
    const gridItem = _.find(this.gridConfigTemp, { sortProperty: sortInfo.sortProperty });
    return gridItem ? [{ dir: (sortInfo.ascending ? 'asc' : 'desc'), field: gridItem.dataIndex }] : this.state.sort;
  };

  /**
   * Mapping method for grid config
   */
  gridConfigMapMethod = (column: COLUMN, index: number) => {
    let dataIndex: string = column.dataIndex;
    let rendererKey = dataIndex.split('.')[0];
    let header = column.header;
    let headerCell = header ? false : () => <span />;
    if (_.startsWith(column.sortProperty, 'custom') && _.find(this.state.customFieldsMappers, { 'name': rendererKey })) {
      rendererKey = 'customField'
    } else {
      header = this.translate('columnHeaders.' + header);
    }
    let cellRender = this.CELL_RENDER_MAP.hasOwnProperty(rendererKey) ? this.CELL_RENDER_MAP[rendererKey] : this.defaultLoadingCell;
    let orderIndex = column.orderIndex || index;
    return this.createGridColumn(dataIndex, header, cellRender, headerCell, column.width, column.sortable, orderIndex);
  };

  /**
   * Refresh Button click handler, refreshes main grid on current scroll position
   */
  handleRefreshGrid = (ev: React.MouseEvent) => {
    this.checkboxSelectionClear();
    this.dispatch(this.props.actionsNames.refreshGridOnCurrentPosition);
  }

  handleSelection = (currentDataItem: any, options: any, newState: any = {}) => {
    let { selectedData, tasks } = newState;

    this.setState(newState);

    if (!this.props.templatesOnly) {
      const { doubleClick = false } = options;
      selectedData.forEach((item: any) => item.tasks = tasks[item.pageIndex]);
      this.dispatch('selectionChangeWithToolbarUpdate', Position.end, {
        currentModuleName: this.props.taskList ? 'TaskList' : 'SubmissionsList',
        forceOpenDrawer: !!selectedData.length && doubleClick,
        selectedSubmissions: selectedData
      });
    }
  };

  /**
   * Render custom main grid header cell
   */
  headerCellRenderer = (defaultRendering: any, props: GridHeaderCellProps): any => {
    let upd = null;
    if (defaultRendering) {
      const { className, children: defaultRenderChildren } = defaultRendering.props;
      const { children, onClick } = props;
      const filterIcon = _.get(props, 'columnMenuWrapperProps.columnMenu') ?
        <ColumnMenuWrapper {...props.columnMenuWrapperProps} key={`mainGridHeaderMenu-${props.title}`}
                           filterBtnAriaLabel={this.translate('SRL-openMenuBtn')} /> : null;

      let sortIcon = null;
      if (_.isArray(children) && children[0]) {
        sortIcon = React.cloneElement(children[0] as ReactElement, { 'aria-hidden': 'true' });
      }

      let element
      if (props.field === 'instructions') {
        element = (
          <span
            className='k-text-ellipsis'
            title={props.title}
          >
            <span className={'tp-icon tp-info tp-icon-sm !k-d-flex'} />
          </span>
        );
      } else if (props.field === 'notes') {
        element = (
          <span
            className='k-text-ellipsis'
            title={props.title}
          >
            <span className={'tp-icon tp-notes !k-d-flex k-align-items-center'} />
          </span>
        );
      } else if (props.field === 'statusColor') {
        element = (
          <div className={'pd-status-color-column-indicator'}>
            <span className={'sr-only '}>{props.title}</span>
          </div>
        );
      } else if (this.checkboxSelectionEnabled && props.field === 'checkboxSelection') {
        element = (
          <div className='pd-indetermintate-disabled-checkbox tp-icon tp-remove' tabIndex={0}>
            <span className={'sr-only'}>{props.title}</span>
          </div>
        );
      } else {
        let className = 'k-text-ellipsis';
        if (props.field === 'isFavorite') {
          className = `tp-favorite-submission-cell-text ${className}`;
        }

        element = (
          <span
            className={className}
            title={props.title}
          >
            {props.title}
          </span>
        );
      }

      upd = React.createElement(
        'span',
        { className },
        [
          <span
            onClick={onClick}
            key={`maingridheader-${props.title}`}
            className={defaultRenderChildren[0]?.props?.className}
          >
      {element}{sortIcon}
    </span>,
          filterIcon
        ]
      );
    }
    return upd;
  };

  /**
   * Insert additional columns
   */
  insertFixedColumnsConfig = (columns: COLUMN[] = []) => {
    const { templatesOnly } = this.props;

    if (this.checkboxSelectionEnabled) {
      columns.unshift({
        field: 'checkboxSelection',
        header: 'checkboxSelection',
        hidden: false,
        dataIndex: 'checkboxSelection',
        width: 50
      });
    }

    if (!templatesOnly) {
      let statusColorColumn = {
        field: 'statusColor',
        header: 'statusColor',
        hidden: false,
        dataIndex: 'statusColor',
        width: 4
      };

      columns.unshift(statusColorColumn);
    }

    let menuActionColumn = {
      field: 'menuAction',
      header: 'actionCell',
      locked: true,
      hidden: false,
      dataIndex: 'menuAction',
      width: templatesOnly ? 120 : 60
    };

    columns.push(menuActionColumn);

    return columns;
  };


  /**
   * If field is dateField returns true else false
   */
  isFieldDate = (dataIndex: string) => {
    //@ts-ignore
    return !!DateFields[dataIndex]
  };

  /**
   * Returns true if statusColor column should have color, false otherwise
   * Override in child component
   */
  isStatusColorNeeded = (dataItem: any) => {
    return true;
  };

  /**
   * Return loading cell if data is not populated
   */
  loadingCell = (cellProps: GridCellProps) => {
    let result = null;
    if (cellProps.dataItem.submissionId === undefined) {
      result = <td><span className="k-icon k-i-loading" /></td>;
    }

    return result;
  };

  /**
   * Returns default loading cell if data is not passed correctly (If server adds new columns it will show unformatted cells with some of the data instead of giving an error)
   */
  defaultLoadingCell = (cellProps: GridCellProps) => {
    let result;
    let fieldDescription = '';
    if (!cellProps.dataItem.submissionId) {
      result = <td><span className="k-icon k-i-loading" /></td>;
    } else {
      // getting only className from cellProps because a lot of other props are incompatible with <td> element
      result = <td className={cellProps.className}>
        <span className={'sr-only'}>{this.translate(`columnHeaders.${fieldDescription}`) + ' '}</span>
        {defaultCellRender(this.translate, () => null, cellProps)}
      </td>
    }
    return result;
  }

  /**
   * Return columns that can be toggled
   */
  mapGridConfigItems = (): COLUMN[] => {
    const columns: COLUMN[] = this.state.columns;
    const alwaysVisibleColumns: string[] = ['checkboxSelection', 'statusColor', 'isFavorite', 'menuAction'];
    return columns.filter((column: COLUMN) => !alwaysVisibleColumns.includes(column.dataIndex));
  };

  /**
   * Prevents default behavior so files are present on drop
   * @param event
   */
  maskedDragOverHandler = (event: React.DragEvent) => {
    event.preventDefault();
  };

  /**
   * Add event to window resize
   */
  moduleDidMount = () => {
    window.addEventListener('resize', _.debounce(this.windowOnResize, 100));
    window.addEventListener('click', this.closeGridSettingsDropdown);
  };

  /**
   * Set columns order
   */
  onColumnReorder = (e: GridColumnReorderEvent) => {
    const columns: any = this.state.columns.map((column: any, index: number, arr: any[]) => {
      const gridColumn: any = _.find(e.columns, (item: any) => item.field === column.dataIndex);
      let orderIndex = index;
      if (gridColumn) {
        // prevent moving data columns in front/back of the fixed columns (e.g. isFavorite, action... etc)
        const isReorderable = gridColumn.orderIndex > 1 && gridColumn.orderIndex < arr.length - 1;
        orderIndex = isReorderable ? gridColumn.orderIndex : index
      }
      column.orderIndex = orderIndex;
      return column;
    });
    this.setState({ columns });
  };

  /**
   * Set new column width on resize
   */
  onColumnResize = (e: GridColumnResizeEvent) => {
    clearTimeout(this.columnResizeTimer);
    //@ts-ignore _columns is protected prop of grid and we need access to it, so we can have insight in valid column index order.
    const column = e.columns[e.index];
    const index = _.findIndex(this.gridConfigTemp, { dataIndex: column.field });
    this.gridConfigTemp[index].width = column.width;
    this.columnResizeTimer = setTimeout(() => {
      this.setState({ columns: this.gridConfigTemp });
    }, 500);
  };

  /**
   * Drop event handler handler
   * @param event
   */
  onDropHandler = (event: React.DragEvent) => {
    //define in child classes
  };

  /**
   * Favorite checkbox change handler
   */
  onFavoriteChange = (cellProps: GridCellProps) => {
    let dataItem = cellProps.dataItem;
    let field = cellProps.field;

    if (field) {
      dataItem[field] = !dataItem[field];
    }
    this.dispatch('markSubmissionAsFavorite', '', { selectedSubmission: dataItem });
  };


  /**
   * Toggle grid column visibility
   */
  onGridColumnToggle = (item: COLUMN, index: number) => {
    this.columnPopupArrowIndex = index;
    const columns: any = [...this.state.columns];
    const itemIndex: any = _.findIndex(columns, { dataIndex: item.dataIndex });
    columns[itemIndex].hidden = !columns[itemIndex].hidden;
    const dynamicColumnWidth: any = this.calculateDynamicColumnWidth(columns);
    this.setState({ columns, dynamicColumnWidth }, this.setDynamicSize);
  };

  /**
   * Checks if column can be sorted and handles sorting
   */
  onSortChange = ({ sort }: GridSortChangeEvent) => {
    let field = sort[0].field;
    let allowSorting = false;
    //todo find better way to fix this
    // in sortableColumns we have targetLanguage.value and dueDate.date but in column menu column.field is only targetLanguage/dueDate
    if (this.sortableColumns.includes(field)) {
      allowSorting = true;
    } else if (this.sortableColumns.includes(`${field}.value`)) {
      allowSorting = true;
      sort[0] = { ...sort[0], field: `${field}.value` }
    } else if (this.sortableColumns.includes(`${field}.date`)) {
      allowSorting = true;
      sort[0] = { ...sort[0], field: `${field}.date` }
    }

    if (sort.length && allowSorting) {
      this.setState({ sort },
        () => this.dispatch(this.props.actionsNames.refreshGridOnCurrentPosition));
    }
  };


  /**
   * Render checkbox instead of the regular dropdown menu item
   */
  renderGridConfigItem = (props: any, index: number) => {
    let customField = _.find(this.state.customFieldsMappers, { 'name': props.dataIndex });
    const { dataIndex, header, hidden } = props;
    const cls = classNames(
      'k-item k-menu-item',
      {
        'k-selected': !hidden
      }
    )
    return (
      <li
        className={cls}
        tabIndex={0}
        key={`grid-config-item-${dataIndex}`}
        onClick={this.onGridColumnToggle.bind(this, props, index)}
      >
        <span className={'k-link k-menu-link'} role={'presentation'}>
          <PuzzleFakeCheckbox
            checked={!hidden}
            label={customField ? translateCustomAttributes(header) : this.translate(`columnHeaders.${header}`)}
          />
        </span>
      </li>
    )
  };

  /**
   * Renders search bar area differently if templates uses props render, if task list and submission list uses default render
   * if its dashboard puts filters there
   */
  renderSearchBar = () => {
    let searchBar: JSX.Element = <Filters />;
    const {
      initializeUserData,
      isClientView = false,
      isDrawerOpen = false,
      renderSearchBar,
      filtersAsSearchBar,
    } = this.props.searchBarConfig;

    if (!filtersAsSearchBar) {
      searchBar = <Container
        alignItemsCenter
        displayFlex
        justifyContentBetween
        wrap
      >
        <SearchBar
          initializeUserData={initializeUserData}
          isDrawerOpen={isDrawerOpen}
          isClientView={isClientView}
          setSearchBarState={this.setSearchBarState}
        />
      </Container>
      if (!isUndefined(renderSearchBar)) {
        searchBar = renderSearchBar();
      }
    }

    return searchBar

  }
  /**
   * Grid renderer
   */
  renderGrid = () => {
    let {
      columns = [],
      sort,
      keyboardShortcuts,
      selectionLimit
    } = this.state;
    let {
      pageSize,
      rowHeight,
      selectedFieldName,
      title,
      guid,
      compactRowHeight
    } = this.props;
    const { excludeFirstRequest, uniqueField } = this.props;
    const navigationGridData: NAVIGATION_GRID_PROPS | any = {
      id: 'mainNavigationGrid',
      guid,
      uniqueField: uniqueField,
      onAnyKeyDown: this.onAnyKeyDown,
      onRowControls: this.handleRowControls,
      handleSelection: this.handleSelection,
      keyboardShortcuts,
      columns,
      pageSize,
      requestActionName: 'getMainGridData',
      columnsMapMethod: this.gridConfigMapMethod,
      excludeFirstRequest: excludeFirstRequest,
      beforeSelectionChange: (dataItem: any, options: any) => {
        if (!dataItem.selected || (dataItem.selected && options.ctrlKey) || options.doubleClick || options.contextMenuOptions) {
          let optionsParams = _.pick(options, ['doubleClick', 'shiftKey', 'ctrlKey', 'keyboard', 'contextMenuOptions', 'focus']);
          this.dispatch('isSelectionChangeAllowed', {
            buffer: options.keyboard ? 200 : 1,
            stack: true,
            persistent: ['dataItem', 'options']
          }, {
            dataItem,
            options: optionsParams
          });
        }
        return false;
      }
    };
    if (!this.props.templatesOnly) {
      navigationGridData.contextMenu = [ContextMenu, { selectionLimit }];
    }
    if (columns.length) {
      columns.map((column: COLUMN) => column.field = column.dataIndex);
    }
    return (
      <>
        <DragAndDropWrapper
          show={this.showDragAndDrop()}
          dragLeaveHandler={this.dragLeaveHandler}
          onDropHandler={this.onDropHandler}
          dragOverHandler={this.maskedDragOverHandler}
          dNdMessage={this.translate('dropTemplateLabel')}
        />
        <ViewContext.Consumer>
          {({ viewType }) => (
            <NavigationGrid
              {...navigationGridData}
              ref={(el) => {
                this.gridRef = el;
              }}
              accessibilityCaption={title}
              accessibilityRowDescription={this.translate('accessibilityRowDescription')}
              className={`tp-main-grid ${viewType === 'COMPACT' ? 'pd-compact-view' : ''}`}
              emptyGrid={this.props.emptyGridConfig}
              headerCellRender={this.headerCellRenderer}
              onColumnReorder={this.onColumnReorder}
              onColumnResize={this.onColumnResize}
              rowHeight={viewType === 'COMPACT' ? compactRowHeight : rowHeight}
              reorderable
              resizable
              rowRender={this.rowRender}
              selectedField={selectedFieldName}
              sort={sort}
              style={{ height: '0px' }}
            />
          )}
        </ViewContext.Consumer>
        <TpTooltip
          ref={(el: any) => this.tooltip = el}
          anchorElement="target"
          openDelay={1000}
          position={this.props.templatesOnly ? 'bottom' : 'right'}
          // content={(props: any) => <DueDateIndicatorTooltip {...props} />}
        />

        {!columns.length && <div className={'tp-grid-skeleton'} />}
      </>
    )
  }
  /**
   * Renders grid config dashboard buttons
   */
  renderGridConfigDashboardButtons = () => {
    return (
      <>
        <HBox
          flex1={this.props.favoritesOnly}
          justifyContentEnd={this.props.favoritesOnly}
          gap={this.props.favoritesOnly && 2}
        >
          <Container
            displayFlex
            gap={2}
            ref={(el: any) => this.settingsButtonRef = el?.element || el}
          >
            {this.props.gridFilter.renderFilterHeader()}
            <PuzzleButton
              ref={this.mainGridColumnSettings}
              titleText={this.translate('SRL-mainGridColumn')}
              aria-label={this.translate('SRL-mainGridColumn')}
              iconClass="tp-icon tp-columnsNew"
              fillMode="outline"
              themeColor={'primary'}
              className="tp-with-bg"
              onClick={() => {
                // @ts-ignore
                this.setState({ showGridColumnSettings: !this.state.showGridColumnSettings })
              }}
              tooltipPosition={'left'}
              withTooltip
            />
            {!this.props.favoritesOnly && this.renderRefreshButton()}
          </Container>
        </HBox>
        <Popup
          anchorAlign={{ horizontal: 'right', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'right', vertical: 'top' }}
          anchor={this.settingsButtonRef}
          onOpen={this.columnsPopupOnOpen}
          onClose={() => {
            this.columnPopupArrowIndex = 0
            this.mainGridColumnSettings.current?.getDomNode().focus();
          }}
          show={this.state.showGridColumnSettings}
        >
          <ul
            className="k-widget k-reset k-header k-menu k-menu-vertical tp-main-grid-column-list"
            ref={(el: any) => this.gridSettingsDropdownRef = el?.element || el}
          >
            {this.mapGridConfigItems().map(this.renderGridConfigItem)}
          </ul>
        </Popup>
      </>
    )
  }
  /**
   * Renders grid config buttons
   */
  renderGridConfigButtons = () => {
    const { selectionLimit } = this.state;
    return (
      <>
        {this.additionalGridTitle()}
        <HBox
          justifyContentBetween
          gap={2}
          flex1
          className={'tp-page-header-right-section'}
        >
          {this.renderSearchBar()}
          {!this.props.favoritesOnly && <HBox flex1 justifyContentEnd gap={2}>
            <ActionToolbar
              userInfo={_.get(this.props, 'searchBarConfig.initializeUserData.userProfile.userInfo')}
              selectionLimit={selectionLimit}
            />
            {this.renderGridConfigDashboardButtons()}
          </HBox>}
        </HBox>
      </>
    )
  };

  /**
   * Refresh button render
   */
  renderRefreshButton(): JSX.Element {
    return <PuzzleButton
      aria-label={this.translate('SRL-refreshMainGrid')}
      titleText={this.translate('SRL-refreshMainGrid')}
      themeColor={'primary'}
      fillMode="outline"
      className="tp-with-bg"
      iconClass="tp-icon tp-refresh"
      onClick={this.handleRefreshGrid}
      tooltipPosition={'left'}
      withTooltip
    />
  }

  /**
   * Handles any key down. Here we can implement custom keyboard shortcuts.
   * @param e
   */
  onAnyKeyDown = (e: any) => {
    const { key, altKey, shiftKey } = e;
    if (altKey && shiftKey) {
      switch (key) {
        case 'Enter':
          e.preventDefault();
          e.dataItem.selected = false;
          this.gridRef.gridRef?.rowDoubleClick(e);
          break;
        case 'ArrowUp':
          // combination above will focus the drawer
          if (e.dataItem?.submissionId) {
            let drawer = document && document.getElementById(Drawer.drawerId);
            drawer?.setAttribute('data-rowId', e.dataItem.submissionId);
            drawer?.focus();
          }
          break;
        case 'Backspace':
          // combination above will close the drawer
          let isDialogUp: boolean = !!(document && document.activeElement?.classList.contains('tp-modal-dialog-wrapper'));
          if (isDialogUp) {
            let drawer = document && document.getElementById(Drawer.drawerId);
            drawer?.focus();
          } else {
            let closeDrawerBtn = document && document.getElementById('close_drawer');
            closeDrawerBtn?.click();
          }
          break;
        default:
          break;
      }
    }
  };

  /**
   * Priority cell render
   * @param cellProps
   */
  priorityRenderer = (cellProps: GridCellProps) => {
    const prefix = 'priority';
    const priorityEnum: { [key: number]: { locale: string, class: string } } = {
      0: {
        locale: commonLocalizationGetter('PRIORITY.LOW'),
        class: `${prefix}-low`
      },
      1: {
        locale: commonLocalizationGetter('PRIORITY.STANDARD'),
        class: `${prefix}-standard`
      },
      2: {
        locale: commonLocalizationGetter('PRIORITY.RUSH'),
        class: `${prefix}-rush`
      }
    };

    let renderValue = '';
    let priorityClass = '';
    if (cellProps.field) {
      let value: number | null = cellProps.dataItem[cellProps.field];
      if (_.isNumber(value)) {
        renderValue = priorityEnum[value].locale;
        priorityClass = priorityEnum[value].class
      }
    }
    let loadingCell = this.loadingCell(cellProps);
    let tdProps = getTableTdAccessibilityProps(cellProps);

    return (
      loadingCell ? loadingCell :
        (
          <td {...tdProps} className={cellProps.className}>
            <HBox
              className={'pd-priority-status tp-text-medium tp-pointer-events-none ' + priorityClass}
              alignItemsCenter
              justifyContentCenter
              displayInlineFlex
            >
              {renderValue}
            </HBox>
          </td>
        )
    )
  };

  /**
   * Row renderer, add onDoubleClick handler and onContextMenu handler
   */
  rowRender = (trElement: ReactElement<HTMLTableRowElement>, rowProps: GridRowProps) => {
    const elementProps = { ...trElement.props };
    const { submissionStatus } = rowProps.dataItem;
    const inProgress: boolean = submissionStatus && submissionStatus.name === 'PROCESSING';

    if (inProgress) {
      elementProps.className += ' tp-row-disabled';
    }

    let trProps: any = {
      ...elementProps,
      // 'aria-label': `${this.translate('columnHeaders.submissionId')} ${submissionId?.replace(/^0+/g, '')}`
    };

    if (this.state.drawerOpen) {
      trProps['aria-controls'] = Drawer.drawerId
    }
    // @ts-ignore
    return React.cloneElement(trElement, { ...trProps }, elementProps.children);
  };

  /**
   * Select submission
   */
  selectSubmission = (submission: SUBMISSION) => {
    this.setState({
      selectedSubmission: submission
    });
  };

  /**
   * Set data
   */
  setConfigData = (columns: COLUMN[], filterConfig: any, dueSoon: number, readerColumns: READER_COLUMN[], sortInfo: any, cookieParams: any, selectionLimit: number) => {
    const { getDataUrl } = this.props;
    columns = columns.reduce((acc: COLUMN[], item: COLUMN) => {
      if (_.startsWith(item.dataIndex, "chartSegment")) {
        const itemCopy: any = { ...item };
        itemCopy.headerKey = item.header;
        delete itemCopy.header;
        this.chartFiltersConfiguration.push(itemCopy);
      } else {
        acc.push(item);
      }
      return acc;
    }, []);

    const quickFiltersStatus = [
      { name: 'ON_HOLD', value: 'ON_HOLD' },
      { name: 'ACTIVE', value: 'ACTIVE' },
      { name: 'COMPLETED', value: 'COMPLETED' },
      { name: 'DELIVERED', value: 'DELIVERED' }
    ];
    const customFieldsMappers = this.getCustomFieldsMappers(readerColumns);
    const customFields = _.reduce(customFieldsMappers, (obj: any, param) => {
      obj[param.name] = param.name;
      return obj;
    }, {});

    this.GET_DATAINDEX_MAP = {
      ...this.GET_DATAINDEX_MAP,
      ...customFields
    };

    let filterConfigData = this.props.gridFilter.prepareQuickFilterConfig(filterConfig);
    if (getDataUrl === "/submissionsView") {
      filterConfigData = filterConfigData.map((item: any) => {
        if (item.filterName === 'status') {
          item.filterValues = quickFiltersStatus;
        }
        return item;
      });
    }
    this.sortableColumns = this.createSortableColumnsArray(columns);
    columns = this.insertFixedColumnsConfig(columns);
    const dynamicColumnWidth = this.calculateDynamicColumnWidth(columns);
    this.gridConfigTemp = columns;
    const sort = this.props.sortable ? this.getSortObject(sortInfo) : [];
    const isDate = this.isFieldDate(sort[0].field);

    if (isDate) {
      sort[0].field = `${sort[0].field}.date`;
    }
    const { quickFiltersData } = this.state;
    if (cookieParams?.submissionId) {
      quickFiltersData.push({
        field: 'submissionId',
        value: cookieParams.submissionId
      });
    }
    // @ts-ignore
    this.setState({
      quickFiltersData,
      dynamicColumnWidth,
      columns,
      filterConfigData,
      dueSoon,
      sort,
      customFieldsMappers,
      selectionLimit
    }, this.props.gridFilter.createFieldFilters);
  };
  /**
   * Sets grid height on Dashboard page
   */
  setCustomDynamicSize = () => {
    if (this.props.innerRef && this.props.innerRef.current) {
      const current = this.props.innerRef.current;
      const parent = current.parentElement;
      const child = current.firstElementChild;
      const columnsButtonGroupHeight: number = 56;
      const sectionMarginBottom: number = 8;

      let i = current.childElementCount;
      let childrenHeightSum = 0;
      while (i-- > 1) {
        childrenHeightSum += current.children[i].offsetHeight;
      }
      child.style.height = '0px';
      child.style.height = `${parent.offsetHeight - childrenHeightSum - columnsButtonGroupHeight - sectionMarginBottom}px`;

      let tables = current.getElementsByTagName('table');
      let tablesLength = tables.length;
      let currentTable: HTMLElement;
      let currentWidth: number;
      let headerTableWidth = tables[0].offsetWidth;
      while (tablesLength) {
        tablesLength--;
        currentTable = tables[tablesLength];
        currentWidth = currentTable.offsetWidth || headerTableWidth; // force width for empty grid, header table always have offsetWidth
        currentTable.style.width = `${currentWidth}px`;
      }
    }
  }
  /**
   * Sets the height of the grid depending on whether it's on Dashboard or Submission list page
   */
  setDynamicSize = () => this.props.favoritesOnly ? this.setCustomDynamicSize() : this.props.setDynamicSize();

  /**
   * Sets sort that has higher priority than one in state (save of /folderViewConfig)
   */
  setOverrideSort = () => {
    this.overrideSort = {
      dir: 'asc',
      field: 'dueDate'
    }
  };

  /**
   * Triggered from searchbar when search value is updated
   * @param active
   */
  setSearchBarState = (active: boolean): void => {
    // @ts-ignore
    this.setState({
      activeSearchbar: active
    })
  };

  /**
   * Moves checked filter data to the top of the filterValues array
   * @param columnMenuProps
   */
  moveCheckedFilterData = (columnMenuProps: COLUMN_MENU_PROPS) => {
    const filterName = columnMenuProps.filterable.filterName;
    const filterValues = columnMenuProps.filterable.filterValues;
    let checkedElements: any[] = [];
    if (this.state.openFilter === filterName && filterValues) {
      columnMenuProps.values.forEach((value: string) => {
        const foundItem = filterValues.find((item: {
          name: string;
          value: string
        }) => item.value === value);
        if (foundItem) {
          checkedElements.push(foundItem);
          const foundIndex = filterValues.indexOf(foundItem);
          filterValues.splice(foundIndex, 1);
        }
      });
      if (filterName === 'budgetStatus') {
        columnMenuProps.filterable.filterValues.sort((
          first: {
            name: string,
            value: string
          },
          second: {
            name: string,
            value: string
          }) => budgetStatusesOrder.indexOf(first.value) - budgetStatusesOrder.indexOf(second.value));
      } else {
        columnMenuProps.filterable.filterValues = _.sortBy(filterValues, ['name']);
      }
      columnMenuProps.filterable.filterValues.unshift(...checkedElements);
    }
  }

  /**
   * Set's currently openedFilter('' if none is opened)
   * @param columnMenuProps
   * @param name
   */
  setOpenFilter = (columnMenuProps: COLUMN_MENU_PROPS, name: string): void => {
    // @ts-ignore
    this.setState({
      openFilter: name
    }, () => {
      this.moveCheckedFilterData(columnMenuProps)
    })
  }
  /**
   * Show/hide drag and drop placeholder
   */
  showDragAndDrop = () => {
    return false;
  }

  /**
   * Template delete action
   */
  templateDelete = (dataItem: any) => {
    const { submissionId } = dataItem;

    this.dispatch('deleteTemplate', '', { submissionId });
  };

  /**
   * Template edit
   */
  templateEdit = (dataItem: any) => {
    const { submissionId } = dataItem;

    let submissionData = {
      submissionId
    };

    this.dispatch('editTemplate', '', { submissionData });
  };

  /**
   * Update checkbox selection
   * Selection need to be preserved after grid refresh
   */
  updateCheckboxSelection = (gridData: {
    items: any[]
  }) => {
    let { uniqueField } = this.props;
    if (this.checkboxSelectionEnabled) {
      let { items } = gridData;
      let { checkboxSelectionData } = this.state;
      let selectedUniqueValue = checkboxSelectionData?.map((item: any) => item[uniqueField]);

      if (selectedUniqueValue?.length) {
        let i = 0;
        while (i < items.length) {
          let current = items[i];
          if (selectedUniqueValue.includes(current[uniqueField])) {
            current.checkboxSelection = true;
          }
          i++;
        }
      }
    }
  }

  /**
   * Updates data for main grid search with data from filter
   */
  updateDataWithFilterParams = (pageSize: number, pageNum: number, mainGridFilter: any, userName: string, createFilterForm: boolean) => {
    const { pageSize: size, favoritesOnly, templatesOnly } = this.props;

    let props = {
      folder: this.getFolderParamForRequest(),
      size,
      includeHidden: mainGridFilter?.includeHidden || false,
      index: pageNum
    };

    let clonedFilter = { ...mainGridFilter };
    if (clonedFilter?.submissionStatusValues?.includes("CANCELLED")) {
      Object.assign(props, { includeHidden: true });
    }

    if (clonedFilter?.submissionStatusValues) {
      clonedFilter.submissionStatusValues = getMappedSubmissionStatuses(clonedFilter.submissionStatusValues, 'SUBMISSION_STATUSES_MAP_SERVER');
    }
    Object.assign(props, clonedFilter);

    if (favoritesOnly && !createFilterForm) {
      Object.assign(props, { isFavorite: true });
    }

    if (templatesOnly) {
      Object.assign(props, { isTemplate: true });
    }

    return props;
  };

  /**
   * Update grid configuration (columns order, sizes, show/hide,...)
   */
  updateFolderViewConfiguration = () => {
    const params = this.getGridConfigJsonData();
    if (this.state.columns.length) {
      this.dispatch('folderViewUpdate', Position.start, { params });
    }
  }

  /**
   * Updates submission after successful change of favorite flag
   *
   * @param dataItem
   */
  updateMainGridAfterFavoriteChange = (dataItem: SUBMISSION) => {
    let { data } = this.state;
    let arrayIndex = data.findIndex((currentValue) => currentValue === dataItem);
    data[arrayIndex] = dataItem;
    // @ts-ignore
    this.setState({ data });
  };

  /**
   * Updates drawer state
   * @param drawerOpen
   * */
  updateDrawerState = (drawerOpen: boolean) => {
    // @ts-ignore
    this.setState({ drawerOpen })
  }

  /**
   * Method for scrollbar update
   */
  updateScrollbarState = () => {
    const virtualScrollDomNode = document.querySelector('div.k-virtual-content');
    const gridHeader = document.querySelector('div.k-grid-header');
    checkVerticalScroll(virtualScrollDomNode, gridHeader);
  };

  /**
   * Calls corresponding methods on resize such as setDynamicSize and updateScrollbarState
   */
  windowOnResize = () => {
    this.setDynamicSize();
    this.updateScrollbarState();
  };

  renderBody = () => {
    let {
      warningBannerMessage
    } = this.state;
    let {
      additionalClass
    } = this.props;
    const bannerCls = warningBannerMessage ? 'pd-main-grid-topbar-with-banner' : '';
    const taskListCls = classNames(
      'tp-task-list-wrap',
      {
        'pd-main-grid-topbar-with-banner': warningBannerMessage
      }
    )
    let gridComponent = (
      <>
        <Container
          ref={this.gridDomElement}
          alignItemsCenter
          hBox
          wrap
          htmlTag={'section'}
          tabIndex={0}
          className={`tp-page-top-section ${bannerCls}`}
          ariaLabel={this.props.topSectionAriaLabel}
        >
          {this.renderGridConfigButtons()}
        </Container>
        {this.props.favoritesOnly ? <section className={'tp-dashboard-grid-wrapper'}>
          <HBox className={'tp-main-grid-buttons'}>
            <h2 className={'tp-text-medium tp-font-size-xl'}>{this.translate('favoriteSubmissions')}</h2>
            {this.renderGridConfigDashboardButtons()}
          </HBox>
          <div
            tabIndex={0}
            aria-label={this.props.gridSectionAriaLabel}
            ref={this.props.innerRef}
            className={`tp-main-grid-wrap ${additionalClass}`}
            id={'main-grid'}
            onDragOver={this.dragOverHandler}
          >
            {this.renderGrid()}
          </div>
        </section> : <section
          tabIndex={0}
          aria-label={this.props.gridSectionAriaLabel}
          ref={this.props.innerRef}
          className={`tp-main-grid-wrap ${additionalClass}`}
          id={'main-grid'}
          onDragOver={this.dragOverHandler}
        >
          {this.renderGrid()}
        </section>}
      </>
    )

    if (this.props.taskList) {
      gridComponent = (<div className={taskListCls}> {gridComponent} </div>)
    }

    return gridComponent;
  };

  /**
   * Renders notes or instructions icon based on shouldNotesBeRendered param
   * @param cellProps
   * @param shouldNotesBeRendered
   */
  renderNotesOrInstructionIcon = (shouldNotesBeRendered: boolean, cellProps: any) => {
    let { dataItem, field } = cellProps;
    let value;

    if (field) {
      value = dataItem[field];
    }

    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let selectedData = [cellProps.dataItem];
    let openNotesOnDrawer: boolean = false;
    let iconClasses: string = 'tp-info'; // Change icon and size based on shouldNotesBeRendered
    if (shouldNotesBeRendered) {
      openNotesOnDrawer = true;
      iconClasses = 'tp-notes';
    }
    return (
      loadingCell ? loadingCell : (
        <td
          className='tp-pointable tp-overflow-initial'
          {...tdProps}
        >
          {value && <span
            onClick={() => {
              this.notesInstructionsClickHandler(selectedData, openNotesOnDrawer)
            }}
            className={`!k-d-flex tp-icon tp-font-size-xl tp-color-accent !k-mt-0 ${iconClasses} k-align-items-center !k-d-flex`}
          />}
        </td>
      )
    );
  }

  /**
   * Owner/assign to column cell render
   * @param cellProps
   * @param addEmptyLine
   */
  ownerAndAssignToCellRender = (cellProps: GridCellProps, addEmptyLine: boolean = false) => {
    const { dataItem } = cellProps;
    const { owner, submissionId, submissionName, assignedTo } = dataItem;

    let field = cellProps.field;
    let owners;
    let value: string = '';
    if (field) {
      owners = dataItem[field];
      let ownersNames = owners?.map((owner: OWNER) => owner.name);
      value = ownersNames?.join(', \n');
      if (!value && addEmptyLine) {
        value = '-';
      }
    }
    let loadingCell = this.loadingCell(cellProps);
    const tdProps = getTableTdAccessibilityProps(cellProps);
    const cellHasHyperlinkClass = PoliciesManager.hasHyperlinkAssigneeNamePolicy ? 'pd-cell-with-small-h-padding' : '';
    const multiple = owner?.length > 1 || assignedTo?.length > 1;
    const ownerAndAssignValue = multiple ? this.translate('multiple') : value;
    const emailClientParams: EMAIL_CLIENT_PARAMS = {
      recipients: [this.props.taskList ? assignedTo?.map((assignedTo: PHASE_OWNER) => assignedTo.email) : owner?.map((owner: PHASE_OWNER) => owner.email)],
      subject: `${submissionId} ${submissionName}`,
      message: ''
    }

    return (
      loadingCell ? loadingCell :
        (
          <td {...tdProps} className={`${cellProps.className} ${cellHasHyperlinkClass}`}>
            <OpenMailLink
              emailClientParams={emailClientParams}
              policy={'hasHyperlinkProjectManagerPolicy'}
              titleText={multiple ? value : null}
              tooltipPosition={'right'}
              withTooltip={multiple}
            >
              {ownerAndAssignValue}
            </OpenMailLink>
          </td>
        ))
  };


  /**
   * Click handler for notes and instructions icons on grid
   * @param selectedData
   * @param openNotesOnDrawer
   */
  notesInstructionsClickHandler = (selectedData: any, openNotesOnDrawer: boolean) => {
    let currentModuleName: string = 'SubmissionsList';
    let tabToOpen: number = 1;
    if (this.props.taskList) {
      currentModuleName = 'TaskList';
      tabToOpen = 0;
    }
    this.dispatch('selectionChangeWithToolbarUpdate', Position.end, {
      currentModuleName: currentModuleName,
      selectedSubmissions: selectedData,
      forceOpenDrawer: !!selectedData.length,
      tab: { selected: tabToOpen },
      filters: {},
      openNotes: openNotesOnDrawer,
      calledFromNotesOrInstructionIcon: true
    });
  }

  /**
   * Map for custom cell renderers. Matching with dataIndex from column object
   */
  CELL_RENDER_MAP: any = {};

  /**
   * Map for quick filters. Key is 'filter.name' and value is matching column's 'dataIndex'
   * note: Since some dataIndexes includes dot, those were split by '.' and first word is taken here
   */
  GET_DATAINDEX_MAP: any = {};
  /**
   * Map for quick filters. Key is column's 'dataIndex' and value is matching 'filter.name'
   * note: Since some dataIndexes includes dot, those were split by '.' and first word is taken here
   */
  GET_FILTERNAME_MAP = {};
}

export default Grid;
